'use client';

import { type ReactNode, useEffect, useRef } from 'react';
import {
  applyPrivateShelterTheme,
  readPersistedShelterBranding,
  shelterBrandingStorageKey,
} from '../../../theme/privateShelterBranding';
import {
  isAuthenticatedSession,
  useAuthSession,
} from '../../auth/hooks/useAuthSession';

/**
 * Wraps authenticated `/admin` content. `data-app-theme="private"` is required for
 * Tailwind’s semantic color bridge (`globals.css`). Inline `--theme-*` overrides
 * come from Settings → Customization (`readPersistedShelterBranding` + apply).
 *
 * TODO (API): replace `localStorage` with `GET/PATCH /shelters/:id/branding` and hydrate here.
 */
export function PrivateAppThemeRoot({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const { data: session } = useAuthSession();
  const shelterKey = isAuthenticatedSession(session)
    ? shelterBrandingStorageKey(session.data.user.shelterId)
    : shelterBrandingStorageKey(undefined);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const sync = () => {
      const branding = readPersistedShelterBranding(shelterKey);
      applyPrivateShelterTheme(el, branding);
    };

    sync();
    window.addEventListener('ffl-private-shelter-theme-changed', sync);
    return () =>
      window.removeEventListener('ffl-private-shelter-theme-changed', sync);
  }, [shelterKey]);

  return (
    <div
      ref={rootRef}
      className='min-h-screen bg-background text-foreground'
      data-app-theme='private'
    >
      {children}
    </div>
  );
}
